import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Steps, message, Alert } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode.react';

const { Title, Text, Paragraph } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const InitSetup: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [totpInfo, setTotpInfo] = useState<{
    secret: string;
    qrCode: string;
  } | null>(null);

  const onFinish = async (values: {
    username: string;
    displayName: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/init-admin`, {
        username: values.username,
        displayName: values.displayName,
        password: values.password,
      });

      setTotpInfo(response.data.totp);
      setCurrentStep(1);
      message.success('管理员创建成功！请配置 Google 2FA');
    } catch (error: any) {
      message.error(error.response?.data?.error || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    message.success('初始化完成！请使用刚才设置的账号密码登录');
    navigate('/login');
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
      }}
    >
      <Card
        style={{
          maxWidth: 600,
          width: '100%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            🚀 系统初始化
          </Title>
          <Text type="secondary">创建首个超级管理员账号</Text>
        </div>

        <Steps current={currentStep} style={{ marginBottom: 32 }}>
          <Steps.Step title="创建账号" icon={<UserOutlined />} />
          <Steps.Step title="配置 2FA" icon={<SafetyOutlined />} />
          <Steps.Step title="完成" icon={<CheckCircleOutlined />} />
        </Steps>

        {currentStep === 0 && (
          <Form
            name="init-setup"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Alert
              message="首次使用提示"
              description="请创建超级管理员账号。此账号拥有系统最高权限，请妥善保管密码。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              label="登录用户名"
              name="username"
              rules={[
                { required: true, message: '请输入登录用户名' },
                { min: 3, message: '用户名至少 3 个字符' },
                { pattern: /^[a-zA-Z0-9_]+$/, message: '只能包含字母、数字和下划线' },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="admin" />
            </Form.Item>

            <Form.Item
              label="显示名称"
              name="displayName"
              rules={[{ required: true, message: '请输入显示名称' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="系统管理员" />
            </Form.Item>

            <Form.Item
              label="密码"
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少 6 位' },
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="设置登录密码" />
            </Form.Item>

            <Form.Item
              label="确认密码"
              name="confirmPassword"
              rules={[{ required: true, message: '请再次输入密码' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="再次输入密码" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                创建管理员账号
              </Button>
            </Form.Item>
          </Form>
        )}

        {currentStep === 1 && totpInfo && (
          <div>
            <Alert
              message="配置 Google Authenticator"
              description="为了账号安全,系统启用了双因素认证(2FA)。请使用 Google Authenticator 扫描下方二维码。"
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <QRCode value={totpInfo.qrCode} size={200} level="H" />
            </div>

            <Alert
              message="密钥（手动输入）"
              description={
                <div>
                  <Paragraph copyable style={{ marginBottom: 0 }}>
                    {totpInfo.secret}
                  </Paragraph>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    如果无法扫码，请手动输入以上密钥到 Google Authenticator
                  </Text>
                </div>
              }
              type="info"
              style={{ marginBottom: 24 }}
            />

            <div style={{ marginBottom: 16 }}>
              <Title level={5}>📱 配置步骤：</Title>
              <ol>
                <li>在手机上安装 Google Authenticator 应用</li>
                <li>打开应用，点击 "+" 添加账号</li>
                <li>扫描上方二维码或手动输入密钥</li>
                <li>保存后，应用会生成 6 位验证码</li>
                <li>登录时需要输入该验证码</li>
              </ol>
            </div>

            <Button type="primary" onClick={handleComplete} block size="large">
              我已配置完成，前往登录
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default InitSetup;
